import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import GoogleAd from '../components/GoogleAd';
import LanguageSwitcher from '../components/LanguageSwitcher';
import TaskTimer from '../components/TaskTimer';
import StatisticsPanel from '../components/StatisticsPanel';
import './ListView.css';

function ListView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [newTaskText, setNewTaskText] = useState('');
  const [addingTask, setAddingTask] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchList = async () => {
    try {
      const response = await fetch(`/api/lists/${id}`);
      const data = await response.json();

      if (response.ok) {
        setList(data);
        return data; // Retornar data para poder usarla en callbacks
      } else {
        setError(data.error || t('errorNotFound'));
        return null;
      }
    } catch (err) {
      console.error('Error:', err);
      setError(t('errorLoading'));
      return null;
    } finally {
      setLoading(false);
    }
  };

  const toggleTask = async (taskId, currentStatus) => {
    try {
      const response = await fetch(`/api/lists/${id}/tasks/${taskId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          completed: !currentStatus
        }),
      });

      if (response.ok) {
        // Actualizar el estado local
        setList(prevList => {
          const updatedList = {
            ...prevList,
            tasks: prevList.tasks.map(task =>
              task.id === taskId
                ? { ...task, completed: !currentStatus }
                : task
            )
          };

          // Verificar si se completó el 100%
          const allCompleted = updatedList.tasks.every(task => task.completed);
          if (allCompleted && updatedList.tasks.length > 0) {
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 4000);
          }

          return updatedList;
        });
      }
    } catch (err) {
      console.error('Error actualizando tarea:', err);
    }
  };

  const copyToClipboard = () => {
    const url = window.location.href;
    const textToCopy = `${t('shareTitle')}\n${url}\n${t('shareText')}: ${list.title}`;
    
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  const shareList = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: t('shareTitle'),
        text: `${t('shareText')}: ${list.title}`,
        url: url,
      });
    } else {
      copyToClipboard();
    }
  };

  const addNewTask = async (e) => {
    e.preventDefault();
    if (!newTaskText.trim() || addingTask) return;

    setAddingTask(true);
    try {
      const response = await fetch(`/api/lists/${id}/tasks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: newTaskText.trim()
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Agregar nueva tarea a la lista
        setList(prevList => ({
          ...prevList,
          tasks: [...prevList.tasks, data.task]
        }));
        setNewTaskText('');
      } else {
        setError(data.error || t('errorAddingTask'));
      }
    } catch (err) {
      console.error('Error agregando tarea:', err);
      setError(t('errorConnection'));
    } finally {
      setAddingTask(false);
    }
  };

  if (loading) {
    return (
      <div className="list-view">
        <div className="container">
          <div className="loading">
            <div className="spinner"></div>
            <p>{t('loadingText')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="list-view">
        <div className="container">
          <div className="error-container fade-in">
            <div className="error-icon">×</div>
            <h2>{t('errorTitle')}</h2>
            <p>{error}</p>
            <button
              className="button button-primary"
              onClick={() => navigate('/')}
            >
              {t('backButton')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const completedCount = list.tasks.filter(task => task.completed).length;
  const totalCount = list.tasks.length;
  const progress = (completedCount / totalCount) * 100;

  // Ordenar tareas según prioridad:
  // 1. Tarea activa (timer activo) - siempre primero
  // 2. Tareas no completadas (no iniciadas, pausadas, etc.)
  // 3. Tareas completadas - siempre al final
  const sortedTasks = [...list.tasks].sort((a, b) => {
    const aIsActive = a.timer_status === 'active';
    const bIsActive = b.timer_status === 'active';
    
    // Prioridad 1: Tarea activa siempre primero
    if (aIsActive && !bIsActive) return -1;
    if (!aIsActive && bIsActive) return 1;
    
    // Prioridad 2: Tareas completadas siempre al final
    if (a.completed && !b.completed) return 1;
    if (!a.completed && b.completed) return -1;
    
    // Prioridad 3: Mantener orden original por position dentro de la misma categoría
    return a.position - b.position;
  });

  return (
    <div className="list-view">
      {/* Mensaje de éxito */}
      {showSuccess && (
        <div className="success-message fade-in">
          <div className="success-content">
            <div className="success-icon">✓</div>
            <div className="success-text">
              <h3>{t('successTitle')}</h3>
              <p>{t('successText')}</p>
            </div>
            <button 
              className="success-button"
              onClick={() => navigate('/')}
            >
              {t('successButton')}
            </button>
          </div>
        </div>
      )}

      <div className="list-header">
        <div className="container">
          <div className="list-header-content">
            <button
              className="back-button"
              onClick={() => navigate('/')}
            >
              {t('backButton')}
            </button>
            <LanguageSwitcher />
          </div>
        </div>
      </div>

      <div className="container">
        <div className="list-content">
          <div className="card list-card fade-in">
            <div className="list-title-section">
              <h1 className="list-title">{list.title}</h1>
              <div className="list-meta">
                <span className="list-date">
                  {new Date(list.createdAt).toLocaleDateString('es-ES', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
            </div>

            <div className="progress-section">
              <div className="progress-info">
                <span className="progress-text">
                  {completedCount} {t('progressText')} {totalCount} {t('progressCompleted')}
                </span>
                <span className="progress-percentage">
                  {Math.round(progress)}%
                </span>
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>

            {/* Panel de estadísticas */}
            <div className="statistics-section">
              <button
                className="statistics-toggle-button"
                onClick={() => setShowStatistics(!showStatistics)}
              >
                {showStatistics ? t('statisticsHide') : t('statisticsShow')}
              </button>
              {showStatistics && (
                <StatisticsPanel tasks={list.tasks} />
              )}
            </div>

            {/* Formulario para agregar nuevas tareas */}
            <form onSubmit={addNewTask} className="add-task-form">
              <div className="add-task-input-wrapper">
                <input
                  type="text"
                  className="add-task-input"
                  placeholder={t('addTaskPlaceholder')}
                  value={newTaskText}
                  onChange={(e) => setNewTaskText(e.target.value)}
                  disabled={addingTask}
                />
                <button
                  type="submit"
                  className="add-task-button"
                  disabled={!newTaskText.trim() || addingTask}
                >
                  {addingTask ? t('addingTask') : t('addTaskButton')}
                </button>
              </div>
            </form>

            <div className="tasks-list">
              {sortedTasks.map((task, index) => (
                <div
                  key={task.id}
                  className={`task-item slide-in ${task.completed ? 'completed' : ''} ${task.timer_status === 'active' ? 'active' : ''}`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="task-main-content" onClick={() => toggleTask(task.id, task.completed)}>
                    <div className="task-checkbox">
                      {task.completed ? (
                        <span className="checkbox-checked">✓</span>
                      ) : (
                        <span className="checkbox-unchecked"></span>
                      )}
                    </div>
                    <span className="task-text">{task.text}</span>
                  </div>
                  <TaskTimer
                    task={task}
                    listId={id}
                    allTasks={list.tasks}
                    onTimerUpdate={async (updatedTask) => {
                      // Actualizar la tarea específica inmediatamente
                      setList(prevList => ({
                        ...prevList,
                        tasks: prevList.tasks.map(t =>
                          t.id === updatedTask.id ? updatedTask : t
                        )
                      }));
                      
                      // Refrescar toda la lista después de un pequeño delay para obtener el estado actualizado
                      // (importante si otra tarea fue pausada automáticamente)
                      setTimeout(async () => {
                        const updatedList = await fetchList();
                        if (updatedList) {
                          // Si se finalizó y se marcó como completada, verificar si todas están completadas
                          const allCompleted = updatedList.tasks.every(t => t.completed);
                          if (allCompleted && updatedList.tasks.length > 0) {
                            setShowSuccess(true);
                            setTimeout(() => setShowSuccess(false), 4000);
                          }
                        }
                      }, 300);
                    }}
                  />
                </div>
              ))}
            </div>

            <div className="list-actions">
              <button
                className="button button-primary"
                onClick={shareList}
              >
                {copySuccess ? t('copiedButton') : t('shareButton')}
              </button>
            </div>
          </div>

          {/* Google Ads */}
          <div className="ad-section">
            <GoogleAd slot="sidebar" format="vertical" />
          </div>

          <div className="ad-section">
            <GoogleAd slot="bottom-banner" format="horizontal" />
          </div>
        </div>
      </div>

      <footer className="footer">
        <div className="container">
          <p className="footer-content">
            {t('footerText')}{' '}
            <a 
              href="https://www.instagram.com/mikejauregui/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="footer-link"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="currentColor"
                className="instagram-icon"
              >
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
              {t('footerBy')}
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}

export default ListView;

